import React from "react";
import { styled } from "@material-ui/styles";
import { Box, Card, Switch, Chip } from "@material-ui/core";
import Typography from "../typography";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonIcon from "@material-ui/icons/Person";
import { IPlayer } from "../../types";
import { IStyledArguments } from "../../types/styled-arguments";

// Styled Components
const SelectionCard = styled(Card)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(2),
  borderRadius: "16px",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "2px solid rgba(0, 0, 0, 0.08)",
  background: theme.palette.extra.default,
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(1.5),
    borderRadius: "12px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    },
  },
}));

const CardContent = styled(Box)(
  ({ theme }: IStyledArguments) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    position: "relative" as const,
    "&::before": {
      content: '""',
      position: "absolute" as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: "4px",
      background: "transparent",
      transition: "all 0.3s ease",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      gap: theme.spacing(1.5),
    },
  })
);

const CardContentSelected = styled(Box)(
  ({ theme }: IStyledArguments) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    position: "relative" as const,
    "&::before": {
      content: '""',
      position: "absolute" as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: "4px",
      background: theme.palette.primary.main,
      transition: "all 0.3s ease",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      gap: theme.spacing(1.5),
    },
  })
);

const PlayerInfoSection = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    gap: "12px",
  },
}));

const AvatarContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  position: "relative" as const,
  width: "56px",
  height: "56px",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "48px",
    height: "48px",
  },
}));

const Avatar = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "56px",
  height: "56px",
  borderRadius: "50%",
  background: theme.palette.grey[400],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontWeight: 700,
  fontSize: "1.25rem",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  border: "3px solid rgba(255, 255, 255, 0.8)",
  [theme.breakpoints.down("sm")]: {
    width: "48px",
    height: "48px",
    fontSize: "1.1rem",
    border: "2px solid rgba(255, 255, 255, 0.8)",
    "& svg": {
      fontSize: "24px",
    },
  },
}));

const AvatarSelected = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "56px",
  height: "56px",
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontWeight: 700,
  fontSize: "1.25rem",
  transition: "all 0.3s ease",
  border: "3px solid rgba(255, 255, 255, 0.9)",
  [theme.breakpoints.down("sm")]: {
    width: "48px",
    height: "48px",
    fontSize: "1.1rem",
    border: "2px solid rgba(255, 255, 255, 0.9)",
  },
}));

const CheckBadge = styled(Box)(({ theme }: IStyledArguments) => ({
  position: "absolute",
  bottom: "-2px",
  right: "-2px",
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  background: theme.palette.success.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `2px solid ${theme.palette.extra.default}`,
  "& svg": {
    fontSize: "14px",
    color: "white",
  },
  [theme.breakpoints.down("sm")]: {
    width: "20px",
    height: "20px",
    "& svg": {
      fontSize: "12px",
    },
  },
}));

const PlayerDetails = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
}));

const PlayerName = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  fontWeight: 600,
  fontSize: "1rem",
  color: theme.palette.text.primary,
  marginBottom: "4px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
    marginBottom: "2px",
  },
}));

const PlayerMeta = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    gap: "6px",
  },
}));

const StatusChip = styled(Chip)(({ theme }: IStyledArguments) => ({
  height: "24px",
  fontSize: "0.75rem",
  fontWeight: 600,
  background: "rgba(158, 158, 158, 0.1)",
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.grey[300]}`,
  "& .MuiChip-label": {
    padding: "0 10px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "22px",
    fontSize: "0.7rem",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
}));

const StatusChipSelected = styled(Chip)(({ theme }: IStyledArguments) => ({
  height: "24px",
  fontSize: "0.75rem",
  fontWeight: 600,
  background: "rgba(76, 175, 80, 0.1)",
  color: theme.palette.success.main,
  border: `1px solid ${theme.palette.success.main}`,
  "& .MuiChip-label": {
    padding: "0 10px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "22px",
    fontSize: "0.7rem",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
}));

const PositionChip = styled(Chip)(({ theme }: IStyledArguments) => ({
  height: "24px",
  fontSize: "0.75rem",
  fontWeight: 500,
  background: "rgba(33, 150, 243, 0.1)",
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  "& .MuiChip-label": {
    padding: "0 10px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "22px",
    fontSize: "0.7rem",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
}));

const StyledSwitch = styled(Switch)(({ theme }: IStyledArguments) => ({
  width: 62,
  height: 34,
  padding: 7,
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(28px)",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: "#fff",
    width: 32,
    height: 32,
    "&:before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
    },
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: theme.palette.grey[400],
    borderRadius: 20,
  },
  [theme.breakpoints.down("sm")]: {
    width: 52,
    height: 28,
    padding: 5,
    "& .MuiSwitch-switchBase": {
      transform: "translateX(4px)",
      "&.Mui-checked": {
        transform: "translateX(22px)",
      },
    },
    "& .MuiSwitch-thumb": {
      width: 26,
      height: 26,
    },
  },
}));

interface IPlayerSelectionCardProps {
  player: IPlayer;
  selected: boolean;
  onToggle: (playerId: string) => void;
  position?: string;
}

const getPlayerInitials = (player: IPlayer): string => {
  const firstInitial = player.name?.charAt(0)?.toUpperCase() || "";
  const lastInitial = player.surname?.charAt(0)?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
};

const PlayerSelectionCard: React.FC<IPlayerSelectionCardProps> = ({
  player,
  selected,
  onToggle,
  position,
}) => {
  const ContentComponent = selected ? CardContentSelected : CardContent;
  const AvatarComponent = selected ? AvatarSelected : Avatar;
  const StatusChipComponent = selected ? StatusChipSelected : StatusChip;

  return (
    <SelectionCard>
      <ContentComponent>
        <PlayerInfoSection>
          <AvatarContainer>
            <AvatarComponent>
              {selected ? (
                getPlayerInitials(player)
              ) : (
                <PersonIcon style={{ fontSize: "28px" }} />
              )}
            </AvatarComponent>
            {selected && (
              <CheckBadge>
                <CheckCircleIcon />
              </CheckBadge>
            )}
          </AvatarContainer>

          <PlayerDetails>
            <PlayerName>
              {player.name} {player.surname}
            </PlayerName>
            <PlayerMeta>
              <StatusChipComponent
                label={selected ? "Confirmed" : "Available"}
                size="small"
              />
              {position && (
                <PositionChip label={position} size="small" />
              )}
            </PlayerMeta>
          </PlayerDetails>
        </PlayerInfoSection>

        <StyledSwitch
          checked={selected}
          onChange={() => onToggle(player.id)}
          color="primary"
        />
      </ContentComponent>
    </SelectionCard>
  );
};

export default PlayerSelectionCard;
